import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, ShieldCheck, X, Zap, Clock, AlertTriangle } from 'lucide-react';
import { UserTrialStatus } from '../utils/userManagement';

interface TrialLifecyclePromptsProps {
  trialStatus: UserTrialStatus;
  isSubscribed: boolean;
  userEmail?: string | null;
  onNavigate: (path: string) => void;
  onOpenReconciler?: () => void;
  onActivePromptChange?: (active: boolean) => void;
}

export function TrialLifecyclePrompts({
  trialStatus,
  isSubscribed,
  userEmail,
  onNavigate,
  onOpenReconciler,
  onActivePromptChange
}: TrialLifecyclePromptsProps) {
  const [activePrompt, setActivePrompt] = useState<'day1' | 'day2' | 'day3' | null>(null);

  useEffect(() => {
    onActivePromptChange?.(!!activePrompt);
  }, [activePrompt, onActivePromptChange]);

  useEffect(() => {
    // If subscribed or owner or expired, do not show in-trial prompts
    if (isSubscribed || trialStatus.isSubscribed || trialStatus.isOwner || trialStatus.isTrialExpired) {
      setActivePrompt(null);
      return;
    }

    const emailKey = userEmail ? userEmail.replace(/[^a-zA-Z0-9]/g, '_') : 'guest';
    const hoursLeft = trialStatus.hoursRemaining;
    const daysLeft = trialStatus.daysRemaining;

    try {
      // Final 24 hours Prompt (<24h left or <= 1 day)
      if (daysLeft <= 1 || hoursLeft <= 24) {
        const seenFinal = typeof window !== 'undefined' && window.localStorage ? (localStorage.getItem(`azuraos_prompt_final_${emailKey}`) || localStorage.getItem(`azuraos_prompt_day3_${emailKey}`)) : null;
        if (!seenFinal) {
          setActivePrompt('day3');
          return;
        }
      } 
      // Mid-trial Prompt (2 to 4 days left)
      else if (daysLeft <= 4 && daysLeft >= 2) {
        const seenMid = typeof window !== 'undefined' && window.localStorage ? (localStorage.getItem(`azuraos_prompt_mid_${emailKey}`) || localStorage.getItem(`azuraos_prompt_day2_${emailKey}`)) : null;
        if (!seenMid) {
          setActivePrompt('day2');
          return;
        }
      } 
      // Early Trial Welcome Prompt (Days 5 to 7)
      else if (daysLeft > 4) {
        const seenWelcome = typeof window !== 'undefined' && window.localStorage ? (localStorage.getItem(`azuraos_prompt_welcome_${emailKey}`) || localStorage.getItem(`azuraos_prompt_day1_${emailKey}`)) : null;
        if (!seenWelcome) {
          setActivePrompt('day1');
          return;
        }
      }
    } catch {
      // Gracefully ignore storage exceptions in Incognito
    }
  }, [trialStatus, isSubscribed, userEmail]);

  const dismissPrompt = (promptType: 'day1' | 'day2' | 'day3') => {
    const emailKey = userEmail ? userEmail.replace(/[^a-zA-Z0-9]/g, '_') : 'guest';
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(`azuraos_prompt_${promptType}_${emailKey}`, 'true');
        if (promptType === 'day1') localStorage.setItem(`azuraos_prompt_welcome_${emailKey}`, 'true');
        if (promptType === 'day2') localStorage.setItem(`azuraos_prompt_mid_${emailKey}`, 'true');
        if (promptType === 'day3') localStorage.setItem(`azuraos_prompt_final_${emailKey}`, 'true');
      }
    } catch {}
    setActivePrompt(null);
  };

  if (!activePrompt) return null;

  return (
    <div className="fixed bottom-20 right-4 sm:right-6 z-40 max-w-md w-full animate-slide-in pointer-events-auto">
      {activePrompt === 'day1' && (
        <div 
          id="trial-day1-prompt"
          className="bg-[#121618] border border-[#14B8A6]/40 rounded-2xl p-5 shadow-2xl space-y-3 relative overflow-hidden backdrop-blur-md"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/30 text-[#14B8A6] flex items-center justify-center shrink-0">
                <Sparkles size={16} />
              </div>
              <div>
                <span className="text-[10px] font-mono font-bold text-[#14B8A6] uppercase tracking-wider block">
                  7-DAY PRO EVALUATION ACTIVE
                </span>
                <h4 className="text-sm font-bold text-white">Know What You Keep After Tax Reserves</h4>
              </div>
            </div>
            <button
              onClick={() => dismissPrompt('day1')}
              className="text-gray-500 hover:text-gray-300 p-1 transition-colors cursor-pointer"
              title="Dismiss"
            >
              <X size={15} />
            </button>
          </div>

          <p className="text-xs text-gray-300 leading-relaxed">
            AzuraOS turns messy financial inputs into a decision-ready margin view in under 10 minutes. Set up your revenue streams or drop a bank statement to see your net safe spendable cash.
          </p>

          <div className="pt-1 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  dismissPrompt('day1');
                  onNavigate('/dashboard?action=manage-streams');
                }}
                className="px-3.5 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm shadow-[#14B8A6]/20 active:scale-95"
              >
                <span>Setup Revenue Streams</span>
                <ArrowRight size={13} />
              </button>

              <button
                onClick={() => {
                  dismissPrompt('day1');
                  if (onOpenReconciler) {
                    onOpenReconciler();
                  } else {
                    onNavigate('/reconcile');
                  }
                }}
                className="px-3 py-2 bg-[#162020] hover:bg-[#1C2828] border border-[#14B8A6]/40 text-[#14B8A6] hover:text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer active:scale-95"
              >
                <Zap size={13} />
                <span>AI Reconciler</span>
              </button>
            </div>

            <button
              onClick={() => dismissPrompt('day1')}
              className="px-2 py-1.5 text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {activePrompt === 'day2' && (
        <div 
          id="trial-day2-prompt"
          className="bg-[#121618] border border-teal-500/40 rounded-2xl p-5 shadow-2xl space-y-3 relative overflow-hidden backdrop-blur-md"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-teal-500/20 border border-teal-500/30 text-teal-300 flex items-center justify-center shrink-0">
                <Zap size={16} />
              </div>
              <div>
                <span className="text-[10px] font-mono font-bold text-teal-300 uppercase tracking-wider block">
                  MID-TRIAL INSIGHT
                </span>
                <h4 className="text-sm font-bold text-white">Full operational clarity is active</h4>
              </div>
            </div>
            <button
              onClick={() => dismissPrompt('day2')}
              className="text-gray-500 hover:text-gray-300 p-1 transition-colors cursor-pointer"
              title="Dismiss"
            >
              <X size={15} />
            </button>
          </div>

          <p className="text-xs text-gray-300 leading-relaxed">
            You’ve started mapping your financial architecture. To ensure uninterrupted automated tracking and AI insights once your trial wraps up, lock in your Pro subscription anytime.
          </p>

          <div className="pt-1 flex items-center justify-between gap-2">
            <button
              onClick={() => {
                dismissPrompt('day2');
                onNavigate('/billing');
              }}
              className="px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95"
            >
              <span>Upgrade to Pro via Dodo</span>
              <ArrowRight size={13} />
            </button>
            <button
              onClick={() => dismissPrompt('day2')}
              className="px-3 py-2 text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Continue Exploring
            </button>
          </div>
        </div>
      )}

      {activePrompt === 'day3' && (
        <div 
          id="trial-day3-prompt"
          className="bg-gradient-to-br from-[#1C1410] to-[#121516] border border-amber-500/50 rounded-2xl p-5 shadow-2xl space-y-3 relative overflow-hidden backdrop-blur-md"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0">
                <AlertTriangle size={16} />
              </div>
              <div>
                <span className="text-[10px] font-mono font-bold text-amber-400 uppercase tracking-wider block">
                  FINAL 24 HOURS
                </span>
                <h4 className="text-sm font-bold text-white">Your evaluation ends tomorrow</h4>
              </div>
            </div>
            <button
              onClick={() => dismissPrompt('day3')}
              className="text-gray-500 hover:text-gray-300 p-1 transition-colors cursor-pointer"
              title="Dismiss"
            >
              <X size={15} />
            </button>
          </div>

          <p className="text-xs text-gray-200 leading-relaxed">
            Don't let your financial visibility go dark. Upgrade to Pro now to keep continuous access to your models, projections, and reports.
          </p>

          <div className="pt-1 flex items-center justify-between gap-2">
            <button
              onClick={() => {
                dismissPrompt('day3');
                onNavigate('/billing');
              }}
              className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-black font-extrabold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-md shadow-amber-500/20 active:scale-95"
            >
              <span>Secure Pro Access Now</span>
              <ArrowRight size={13} />
            </button>
            <button
              onClick={() => dismissPrompt('day3')}
              className="px-3 py-2 text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
